/**
 * Unified in-flight promise registry (#490i).
 *
 * Every async continuation that sets React state outside TanStack Query
 * must register its promise here so the test teardown settle hook can
 * await it inside act. Two sources today: lazy chunk loads (lazyNamed.ts)
 * and persist-queue PUTs (usePersistStrategy.ts).
 */

/** What a pending promise is, for the teardown error and its settle budget (#1487). */
export type InflightKind = 'lazy-chunk' | 'persist' | 'msw' | 'other';
export interface InflightMeta {
  kind: InflightKind;
  label: string;
}

/** All promises currently in flight. */
export const pendingInflight = new Set<Promise<unknown>>();

/** Label + kind for every promise in `pendingInflight` (same lifetime). */
export const inflightMeta = new Map<Promise<unknown>, InflightMeta>();

/**
 * Track a promise: add it to the set, remove on settle (resolve or reject),
 * and return the original promise unchanged. `meta` names it in the
 * teardown error and picks its settle budget (lazy chunks get a larger one). Required:
 * an unlabelled entry prints a blank tail in the teardown error (#1487).
 */
export function trackInflight<T>(p: Promise<T>, meta: InflightMeta): Promise<T> {
  pendingInflight.add(p);
  inflightMeta.set(p, { kind: meta.kind, label: meta.label });
  const done = () => {
    pendingInflight.delete(p);
    inflightMeta.delete(p);
  };
  p.then(done, done);
  return p;
}
